import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, Chip, CircularProgress, IconButton, Link, Tooltip, Typography} from "@mui/material";
import {RefreshOutlined, WarningAmberOutlined} from "@mui/icons-material";

import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import {approvalMessage} from "../../util/approvalMessages";
import ApprovalSignatureDialog from "./ApprovalSignatureDialog";
import {signableSlots} from "../../util/approvalSlots";
import {Blue3, Grey5, Grey6} from "../../theme/styles";
import SlotState from "./SlotState";
import RecordReviewDrawer from "./RecordReviewDrawer";

// The approval queue, and signing.
//
// FOUR VIEWS, AND THE FIRST TWO ARE SEPARATE ON PURPOSE. "Awaiting my signature" means a slot of the
// OPEN stage names a persona I hold; "awaiting me later" means one of a later stage does. A queue
// that mixes "you can sign this" with "you will be able to eventually" is one people learn to
// ignore, and a queue people ignore is the failure mode this whole feature is trying to avoid.
//
// BLOCKED IS A VIEW, NOT A HIDDEN STATE. A record whose matrix names a persona nobody holds waits
// until somebody is assigned -- the condition is transient -- but waiting silently is how a queue
// becomes a graveyard.
//
// Design: cres-t5/docs/approvals/design.md

type Row = Record<string, any>;

// ONE BLURB PER VIEW, because one blurb for all four described whichever view was written first.
// "What you sign is the record as it was submitted" is good advice on the two views where you might
// sign and simply untrue on Blocked, which is a list of things nobody can sign at all.
export const VIEWS = [
    {key: "mine", label: "Awaiting my signature", empty: "Nothing is waiting on your signature.",
     blurb: "Records waiting on a signature from you. What you sign is the record as it was"
          + " submitted \u2014 editing it after signing invalidates the signatures, so approvals"
          + " cover what was actually reviewed."},
    {key: "later", label: "Awaiting me later", empty: "Nothing is coming to you from a later stage.",
     blurb: "Records you will be asked to sign, once the stages before yours are done. Nothing"
          + " here needs you yet."},
    {key: "submitted", label: "Submitted by me", empty: "You have not sent anything out for approval.",
     blurb: "Approvals you sent out, and how far they have got. You can withdraw one that has not"
          + " been decided."},
    {key: "blocked", label: "Blocked", empty: "Nothing is stuck.",
     blurb: "Submitted, but nobody can act on them \u2014 usually because the persona a slot names"
          + " is held by nobody, or only by the person who wrote the record. They will sit here"
          + " until the matrix or the memberships change."},
];

export function ageInStage(request: Row, now: number) {
    // AGE IN THIS STAGE, not age of the request. A request four days old whose current stage opened
    // an hour ago is not four days late, and phase 2's escalation clocks read the same column.
    const opened = request.StageOpenedDatetime ?? request.RequestedDatetime;
    if (!opened) return null;
    const started = Date.parse(String(opened).replace(" ", "T") + "Z");
    if (Number.isNaN(started)) return null;

    const hours = Math.floor((now - started) / 3600000);
    if (hours < 1) return "just now";
    if (hours < 24) return `${hours}h in stage`;
    return `${Math.floor(hours / 24)}d in stage`;
}

export default function ApprovalsSection() {
    const [view, setView] = useState("mine");
    // BUILT FROM THE SESSION, as every t1 screen does: the token lives on the
    // session context and there is no module-level instance to import here.
    const sessionContext = useSessionContext();
    const api = React.useMemo(
        () => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);
    const [requests, setRequests] = useState<Row[]>([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<string | null>(null);
    const [signing, setSigning] = useState<{request: Row; slot: Row} | null>(null);
    const [reviewing, setReviewing] = useState<Row | null>(null);
    const [busy, setBusy] = useState(false);

    /**
     * Where the records are, when THIS view has none.
     *
     * "Nothing here." is three different facts wearing one sentence: no approvals exist at all,
     * some exist but none name you, or one names you and you wrote it -- which is the case that
     * sent somebody looking for a bug. The author of a record cannot sign it, deliberately, so
     * their queue is empty and their submission is sitting one tab away saying nothing.
     *
     * ASKED ONLY WHEN THIS VIEW IS EMPTY, so the ordinary path costs one request as before. Three
     * extra reads on a screen that is otherwise showing a dashed box is a fair trade for not
     * having to guess which of the three facts you are looking at.
     */
    const [elsewhere, setElsewhere] = useState<{key: string; label: string; count: number}[]>([]);

    const load = useCallback(() => {
        setLoading(true);
        setElsewhere([]);
        api({method: "GET", url: "t1/v1/object_approval_requests", params: {View: view}})
            .then(response => {
                const rows = response.data?.Requests ?? [];
                setRequests(rows);
                if (rows.length) return;
                return Promise.all(VIEWS.filter(one => one.key !== view).map(one =>
                    api({method: "GET", url: "t1/v1/object_approval_requests",
                         params: {View: one.key}})
                        .then(other => ({key: one.key, label: one.label,
                                         count: (other.data?.Requests ?? []).length}))
                        // A VIEW THAT WILL NOT LOAD IS NOT AN ERROR HERE. This is a hint on an
                        // empty page; failing it loudly would replace "nothing is waiting on you"
                        // with a red banner about a request nobody asked for.
                        .catch(() => ({key: one.key, label: one.label, count: 0}))))
                    .then(counts => setElsewhere(counts.filter(one => one.count > 0)));
            })
            .catch(() => setMessage("Could not load approvals."))
            .finally(() => setLoading(false));
    }, [api, view]);

    useEffect(() => { load(); }, [load]);

    const sign = (decision: "APPROVE" | "REJECT", comment: string, password: string) => {
        if (!signing) return;
        setBusy(true);
        setMessage(null);
        api({method: "PUT", url: "t1/v1/object_approval_requests", data: {
            RequestUSID: signing.request.USID,
            SlotUSID: signing.slot.USID,
            Decision: decision,
            Comment: comment || null,
            Password: password,
        }})
            .then(() => { setSigning(null); load(); })
            .catch(error => setMessage(approvalMessage(error, "Could not record that.")))
            .finally(() => setBusy(false));
    };

    const now = Date.now();

    // NO WIDTH CAP. At 1000px every item in a queue row was squeezed -- a flex child shrinks below
    // its own content unless told not to -- so "SOP-00124" wrapped mid-token and both buttons broke
    // across two lines on a window with room to spare. The row below says what may shrink; the page
    // no longer decides for it.
    return (
        <div style={{margin: "16px 30px 40px 30px"}}>
            <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 10}}>
                {VIEWS.map(one => (
                    <Button key={one.key} size="small" onClick={() => setView(one.key)}
                            variant={view === one.key ? "outlined" : "text"}
                            sx={{textTransform: "none", fontSize: 13}}>
                        {one.label}
                    </Button>
                ))}

                {/* REFRESH, AFTER THE VIEWS. This queue is the one screen where what you are
                    looking at goes stale because of what somebody ELSE did -- a stage opening, a
                    co-approver signing, a submission being withdrawn -- and nothing here polls. The
                    alternative was reloading the whole page, which loses the view you had picked.
                    Re-reads the CURRENT view; it is not a view of its own, so it sits apart from
                    the four rather than beside them as a fifth. */}
                <Tooltip title="Re-read this queue">
                    <span style={{marginLeft: 4}}>
                        <IconButton size="small" onClick={load} disabled={loading || busy}
                                    aria-label="Refresh">
                            <RefreshOutlined fontSize="small"/>
                        </IconButton>
                    </span>
                </Tooltip>
            </div>

            {/* NO 78ch CAP. It was there to keep prose at a readable measure, but the longest
                blurb is ~200 characters and 78ch is ~500px, so the sentence Ed reads most often
                broke across three lines under the buttons and read as a paragraph of small print
                rather than a one-line caption. Full width puts it on one line at any ordinary
                window and still wraps, rather than overflowing, on a narrow one. */}
            <Typography style={{fontSize: 13, color: Grey5, marginBottom: 16}}>
                {VIEWS.find(one => one.key === view)?.blurb}
            </Typography>

            {message ? (
                <Typography style={{fontSize: 13, color: "#B42318", marginBottom: 12}}>{message}</Typography>
            ) : null}

            {loading ? <CircularProgress size={20}/> : requests.length === 0 ? (
                <Box sx={{border: "1px dashed #D8DDE4", borderRadius: "8px", padding: "32px",
                          textAlign: "center"}}>
                    <Typography style={{fontSize: 14, color: Grey6}}>
                        {VIEWS.find(one => one.key === view)?.empty ?? "Nothing here."}
                    </Typography>
                    {/* AND WHERE THEY ARE INSTEAD. Without this the author of a record reads an
                        empty queue as "no approvals exist", goes looking for the submission that
                        is one tab away, and concludes something is broken. The tabs carry no
                        counts, so there is nothing else on the screen that would tell them. */}
                    {elsewhere.length > 0 && (
                        <Typography style={{fontSize: 13, color: Grey6, marginTop: 10}}>
                            {elsewhere.map((one, index) => (
                                <React.Fragment key={one.key}>
                                    {index === 0 ? "" : index === elsewhere.length - 1 ? " and " : ", "}
                                    <Link component="button" type="button"
                                          onClick={() => setView(one.key)}
                                          style={{fontSize: 13}}>
                                        {one.count} in {one.label}
                                    </Link>
                                </React.Fragment>
                            ))}
                            .
                        </Typography>
                    )}
                </Box>
            ) : (
                <div style={{border: "1px solid #E4E7EC", borderRadius: 6}}>
                    {requests.map((request, index) => {
                        const mine = signableSlots(request);
                        return (
                            <div key={request.USID}
                                 style={{padding: "12px 16px",
                                         borderTop: index ? "1px solid #E4E7EC" : undefined}}>
                                <div style={{display: "flex", alignItems: "center", gap: 10}}>
                                    {/* THE NUMBER FIRST. "SOP-00124" is what a reviewer recognises
                                        and can cross-check; a title alone asks somebody to attest to
                                        a record they cannot look up. Reported by Ed, reviewing as a
                                        Document Control Board member. */}
                                    {request.RecordObjectID ? (
                                        <Typography style={{fontSize: 14, fontWeight: 700, color: Blue3,
                                                            whiteSpace: "nowrap", flexShrink: 0}}>
                                            {request.RecordObjectID}
                                        </Typography>
                                    ) : null}
                                    <Typography title={request.RecordName}
                                                style={{fontSize: 14, fontWeight: 500, minWidth: 0,
                                                        overflow: "hidden", textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap"}}>
                                        {request.RecordName}
                                    </Typography>
                                    {/* The TYPE is worth a chip; "Object" is not -- it names an
                                        internal target kind and tells a reviewer nothing. */}
                                    {request.TargetType && request.TargetType !== "Object" ? (
                                        <Chip size="small" label={request.TargetType}
                                              sx={{fontSize: 11, height: 20, flexShrink: 0}}/>
                                    ) : null}
                                    {request.ObjectTypeName ? (
                                        <Chip size="small" label={request.ObjectTypeName}
                                              sx={{fontSize: 11, height: 20, flexShrink: 0}}/>
                                    ) : null}
                                    {request.Status !== "PENDING" ? (
                                        <Chip size="small" label={request.Status.toLowerCase()}
                                              sx={{fontSize: 11, height: 20, flexShrink: 0}}/>
                                    ) : null}
                                    <Typography style={{fontSize: 12, color: Grey6,
                                                        whiteSpace: "nowrap", flexShrink: 0}}>
                                        rev {request.RecordRevision} · stage {request.CurrentStage} ·{" "}
                                        {request.SlotsSigned}/{request.SlotsTotal} signed ·{" "}
                                        {ageInStage(request, now)}
                                    </Typography>
                                    <div style={{flex: 1, minWidth: 8}}/>
                                    {/* READ BEFORE SIGN, and in that order on the screen too. The
                                        review button sits to the LEFT of the signing buttons so the
                                        reading comes first in the reading order, not as an
                                        afterthought beside the commitment. */}
                                    <Button size="small" onClick={() => setReviewing(request)}
                                            sx={{textTransform: "none", fontSize: 13,
                                                 whiteSpace: "nowrap", flexShrink: 0}}>
                                        Review the record
                                    </Button>
                                    {mine.map((slot: Row) => (
                                        <Button key={slot.USID} size="small" variant="outlined"
                                                onClick={() => setSigning({request, slot})}
                                                sx={{textTransform: "none", fontSize: 13,
                                                     whiteSpace: "nowrap", flexShrink: 0}}>
                                            Sign as {slot.Meaning}
                                        </Button>
                                    ))}
                                </div>

                                {request.IsBlocked ? (
                                    <Typography style={{fontSize: 12, color: "#B54708", marginTop: 6,
                                                        display: "flex", alignItems: "center", gap: 4}}>
                                        <WarningAmberOutlined sx={{fontSize: 14}}/>
                                        {request.BlockedReason}
                                    </Typography>
                                ) : null}

                                <div style={{marginTop: 6, display: "flex", gap: 14, flexWrap: "wrap"}}>
                                    {(request.Slots ?? []).map((slot: Row) => (
                                        <SlotState key={slot.USID} slot={slot}
                                                   currentStage={request.CurrentStage}/>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <RecordReviewDrawer open={!!reviewing} request={reviewing}
                                onClose={() => setReviewing(null)}/>

            <ApprovalSignatureDialog
                open={!!signing}
                request={signing?.request}
                slot={signing?.slot}
                busy={busy}
                onSign={(decision: "APPROVE" | "REJECT", comment: string, password: string) => sign(decision, comment, password)}
                onClose={() => setSigning(null)}/>

        </div>
    );
}
